import clsx from "clsx"
import { Fragment, useCallback, useMemo, useState } from "react"

import useHorizontalScrollFade from "@hooks/useHorizontalScrollFade"
import useIsDesktop from "@hooks/useIsDesktop"

import { faIcon } from "@utils/faIcon"

import { LinkButton } from "@atoms"

import { Select, TabStack } from "@molecules"

import { ViewBarTabsContext } from "./ViewBarTabs.context"
import styles from "./ViewBarTabs.module.scss"
import {
    ViewBarTabRegistration,
    ViewBarTabsProps,
    ViewBarTabsSelectProps,
    ViewBarTabsStripProps,
} from "./ViewBarTabs.types"

function ViewBarTabsStrip(props: ViewBarTabsStripProps) {
    const { children, isVisible } = props

    const { containerRef, scrollRef, isLeftClipped, isRightClipped } = useHorizontalScrollFade<HTMLDivElement>()

    return (
        <div
            ref={containerRef}
            hidden={!isVisible}
            className={clsx(styles.tabsContainer, {
                [styles.leftClipped]: isLeftClipped,
                [styles.rightClipped]: isRightClipped,
            })}
        >
            <div className={styles.tabsScroll} ref={scrollRef}>
                <TabStack variant="default" size="md">
                    {children}
                </TabStack>
            </div>
        </div>
    )
}

function ViewBarTabsSelect(props: ViewBarTabsSelectProps) {
    const { tabs } = props

    const [isOpen, setIsOpen] = useState(false)

    const selectedTab = tabs.find((tab) => tab.isSelected)

    // Phases in first-seen order; each phase becomes a divided group in the select list.
    const phaseOrder = [...new Set(tabs.map((tab) => tab.phase))]

    return (
        <Select.Root isOpen={isOpen} onOpenChange={setIsOpen} mobilePresentation="inline">
            <Select.Trigger>
                <LinkButton
                    colorScheme="black"
                    rightIcon={faIcon({ name: "angles-up-down", style: "regular" })}
                    noDebounce={true}
                    onClick={() => {}}
                    isPressed={isOpen}
                >
                    <span>{selectedTab?.label}</span>
                    {selectedTab?.count !== undefined && <span>{selectedTab.count}</span>}
                </LinkButton>
            </Select.Trigger>
            <Select.Viewport>
                <Select.List title="Navigate">
                    {phaseOrder.map((phase, index) => (
                        <Fragment key={phase}>
                            {index > 0 && <Select.ListGroupDivider />}
                            <Select.ListGroup>
                                {tabs
                                    .filter((tab) => tab.phase === phase)
                                    .map((tab) => (
                                        <Select.ListItem
                                            key={tab.value}
                                            variant="default"
                                            item={{ label: tab.label, value: tab.value }}
                                            isSelected={tab.isSelected}
                                            onSelect={tab.onSelect}
                                        />
                                    ))}
                            </Select.ListGroup>
                        </Fragment>
                    ))}
                </Select.List>
            </Select.Viewport>
        </Select.Root>
    )
}

export default function ViewBarTabs(props: ViewBarTabsProps) {
    const { children, activePhase } = props

    const isDesktop = useIsDesktop()
    const [registrations, setRegistrations] = useState<ViewBarTabRegistration[]>([])

    // Tabs keep their mount order across refreshes so the select lists them as composed.
    const registerTab = useCallback((tab: ViewBarTabRegistration) => {
        setRegistrations((current) => {
            const index = current.findIndex((registration) => registration.value === tab.value)
            if (index === -1) {
                return [...current, tab]
            } else {
                return current.map((registration, position) => (position === index ? tab : registration))
            }
        })
    }, [])

    const unregisterTab = useCallback((value: string) => {
        setRegistrations((current) => current.filter((registration) => registration.value !== value))
    }, [])

    const contextValue = useMemo(
        () => ({ registerTab, unregisterTab, activePhase }),
        [registerTab, unregisterTab, activePhase],
    )

    const visibleTabs = registrations.filter((registration) => !registration.isHidden)

    return (
        <ViewBarTabsContext.Provider value={contextValue}>
            {!isDesktop && <ViewBarTabsSelect tabs={visibleTabs} />}
            {/* The strip stays mounted (hidden) on mobile so a breakpoint change never remounts the tabs, whose
                registrations and data live in them; on mobile each tab renders nothing and the select stands in. */}
            <ViewBarTabsStrip isVisible={isDesktop}>{children}</ViewBarTabsStrip>
        </ViewBarTabsContext.Provider>
    )
}
